import { useParams } from 'react-router-dom';
import { KeyRound, UsersRound } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Badge, Skeleton, Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useIamUser,
  useUserPolicyMutation,
  s3ExtractError,
} from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../../S3ConsoleBreadcrumbs';
import { Mono } from '../../s3-parts';
import { PolicyAttachments } from '../PolicyAttachments';
import { formatIamDate } from '../iam-format';

/** IAM user detail — attached policies (editable), group memberships and keys. */
export function UserDetail({ serviceId }: { serviceId: string }) {
  const toast = useToast();
  const { name = '' } = useParams<{ name: string }>();
  const userName = decodeURIComponent(name);
  const { data, isLoading } = useIamUser(serviceId, userName);
  const attach = useUserPolicyMutation(serviceId, true);
  const detach = useUserPolicyMutation(serviceId, false);

  const runPolicy = (m: typeof attach, policyArn: string, ok: string, fail: string) =>
    m.mutate(
      { name: userName, policyArn },
      { onSuccess: () => toast.success(ok), onError: (e) => toast.error(s3ExtractError(e, fail)) },
    );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <S3ConsoleBreadcrumbs trail={[{ label: 'Users', slug: 'iam/users' }, { label: userName }]} />
        }
        title={userName}
        description="IAM user policies, group memberships and access keys."
      />

      {isLoading ? (
        <Skeleton variant="rect" height={320} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Attached policies</CardTitle>
            </CardHeader>
            <CardBody>
              <PolicyAttachments
                serviceId={serviceId}
                attached={data?.attached_policies ?? []}
                pending={attach.isPending || detach.isPending}
                onAttach={(arn) => runPolicy(attach, arn, 'Policy attached', 'Failed to attach policy')}
                onDetach={(arn) => runPolicy(detach, arn, 'Policy detached', 'Failed to detach policy')}
              />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Group memberships</CardTitle>
            </CardHeader>
            <CardBody>
              {data?.groups?.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
                  {data.groups.map((g) => (
                    <Badge key={g.group_name} tone="neutral" appearance="subtle">
                      {g.group_name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<UsersRound size={20} />}
                  title="No groups yet"
                  description="Add this user from a group's page."
                />
              )}
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Access keys</CardTitle>
            </CardHeader>
            <CardBody>
              {data?.access_keys?.length ? (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--vn-space-2)' }}>
                    {data.access_keys.map((k) => (
                  <li
                    key={k.access_key_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--vn-space-3)',
                      border: '1px solid var(--vn-border-subtle)',
                      borderRadius: 'var(--vn-radius-md)',
                      padding: 'var(--vn-space-2) var(--vn-space-3)',
                    }}
                  >
                    <Mono>{k.access_key_id}</Mono>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
                      <Badge tone={k.status === 'Active' ? 'success' : 'neutral'} appearance="subtle" size="sm">
                        {k.status ?? '—'}
                      </Badge>
                      <Mono>{formatIamDate(k.create_date)}</Mono>
                    </span>
                  </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState
                    icon={<KeyRound size={20} />}
                    title="No access keys yet"
                    description="Go to Access Keys to create credentials for this user."
                  />
                )}
            </CardBody>
          </Card>
        </div>
      )}
    </Container>
  );
}
